import { Css, Icon } from "@homebound/beam";
import {
  createDevelopmentChangeLogUrl,
  createDevelopmentDropsUrl,
  createDevelopmentJobLogsUrl,
  createDevelopmentLotSchedulesUrl,
  createDevelopmentLotSummaryUrl,
  createDevelopmentPlanAndOptionsUrl,
  createDevelopmentProcurementUrl,
  createDevelopmentPurchaseOrdersUrl,
  createDevelopmentScheduleUrl,
  createDevelopmentScopeTemplatesUrl,
  createDevelopmentSettingsUrl,
  createDevelopmentTaskLookaheadReportUrl,
  createDevelopmentTeamMembersUrl,
  createDocumentsUrl,
  createProductOfferingsUrl,
} from "src/RouteUrls";
import { SideNavAndRouterConfig, SideNavProps } from "src/components/layout/SideNav";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import { useDevelopmentContext } from "src/routes/developments/context/DevelopmentContext";
import { SideNavLayoutRoute } from "src/routing/components/layouts/SideNavLayoutRoute";
import { developmentPaths } from "src/routing/domains/developmentsPaths";

/**
 * Development SideNav chrome for the data router: builds nav from development context and renders `<Outlet />`.
 *
 * Role: data router pathless layout. Nest under `DevelopmentLayout` in `domains/developments/`.
 */
export function DevelopmentSideNavLayout() {
  const { id, name, lotsAcquired } = useDevelopmentContext();
  const sideNavProps = buildDevelopmentSideNavProps(id, name, lotsAcquired);
  return <SideNavLayoutRoute sideNavProps={sideNavProps} />;
}

function buildDevelopmentSideNavProps(
  developmentId: string | undefined,
  name: string | undefined,
  lotsAcquired: number | undefined,
): SideNavProps {
  if (!developmentId) {
    return { sideNavConfig: [] };
  }

  return {
    sideNavConfig: developmentNavLinks(developmentId),
    contrast: true,
    top: (
      <div css={Css.p3.pb5.$}>
        <div css={Css.df.jcc.aic.bgGray800.br8.wPx(48).hPx(48).gray50.$}>
          <Icon icon="buildingHouse" />
        </div>
        {name && (
          <>
            <h1 css={Css.lg.mt1.gray100.$}>{name}</h1>
            <span css={Css.xs.gray400.$}>{lotsAcquired ?? 0} Lots Acquired</span>
          </>
        )}
      </div>
    ),
  };
}

function developmentNavLinks(developmentId: string): SideNavAndRouterConfig[] {
  return [
    {
      title: "Development",
      items: [
        {
          label: "Overview",
          href: createDevelopmentLotSummaryUrl(developmentId),
          path: `${developmentPaths.lotSummary}/*`,
        },
        { label: "Documents", href: createDocumentsUrl(developmentId), path: developmentPaths.documents },
        { label: "Team Members", href: createDevelopmentTeamMembersUrl(developmentId), path: developmentPaths.team },
        {
          label: "Drops",
          href: createDevelopmentDropsUrl(developmentId),
          path: developmentPaths.drops,
          featureFlag: FeatureFlagType.ProductConfig,
        },
        { label: "Job Logs", href: createDevelopmentJobLogsUrl(developmentId), path: `${developmentPaths.jobLogs}/*` },
      ],
    },
    {
      title: "Schedules",
      items: [
        {
          label: "Development Schedule",
          href: createDevelopmentScheduleUrl(developmentId),
          path: developmentPaths.developmentSchedule,
        },
        {
          label: "Lot Schedules",
          href: createDevelopmentLotSchedulesUrl(developmentId),
          path: developmentPaths.lotSchedules,
        },
        {
          label: "Task Lookahead",
          href: createDevelopmentTaskLookaheadReportUrl(developmentId),
          path: developmentPaths.taskLookaheadReport,
        },
      ],
    },
    {
      title: "Scope",
      items: [
        {
          label: "Plan & Options",
          href: createDevelopmentPlanAndOptionsUrl(developmentId),
          path: developmentPaths.planAndOptions,
        },
        {
          label: "Product Offerings",
          href: createProductOfferingsUrl(developmentId),
          path: developmentPaths.productOfferings,
          featureFlag: FeatureFlagType.ProductConfig,
        },
        {
          label: "Templates",
          href: createDevelopmentScopeTemplatesUrl(developmentId),
          path: developmentPaths.scopeTemplates,
        },
        {
          label: "Change Log",
          href: createDevelopmentChangeLogUrl(developmentId),
          path: `${developmentPaths.changeLog}/*`,
          featureFlag: FeatureFlagType.ChangeRequestMvp,
        },
      ],
    },
    {
      title: "Trades",
      items: [
        {
          label: "Contracts",
          href: createDevelopmentProcurementUrl(developmentId),
          path: developmentPaths.procurement,
        },
        {
          label: "Purchase Orders",
          href: createDevelopmentPurchaseOrdersUrl(developmentId),
          path: developmentPaths.purchaseOrders,
        },
      ],
    },
    {
      items: [
        {
          label: "Settings",
          href: createDevelopmentSettingsUrl(developmentId),
          path: developmentPaths.settings,
          featureFlag: FeatureFlagType.DynamicSchedules,
        },
      ],
    },
  ];
}
